import React from 'react';
import { useTheme } from '@mui/material/styles';

export interface BilldaLogoProps {
  /**
   * Variant of the logo to display
   * - 'primary': Yellow B + dark text (for light backgrounds)
   * - 'secondary': Yellow B + white text (for dark backgrounds)
   * - 'icon': Just the yellow B icon
   * - 'auto': Automatically choose based on theme mode
   */
  variant?: 'primary' | 'secondary' | 'icon' | 'auto';

  /**
   * Width of the logo (height scales proportionally)
   */
  width?: number | string;

  /**
   * Alt text for accessibility
   */
  alt?: string;

  /**
   * Additional CSS class names
   */
  className?: string;
}

/**
 * Billda Logo Component
 *
 * Renders the Billda logo with automatic light/dark mode support.
 *
 * @example
 * // Auto-selects based on theme
 * <BilldaLogo variant="auto" width={200} />
 *
 * @example
 * // Explicit variant for light background
 * <BilldaLogo variant="primary" width={150} />
 *
 * @example
 * // Icon only for navigation
 * <BilldaLogo variant="icon" width={40} />
 */
export const BilldaLogo: React.FC<BilldaLogoProps> = ({
  variant = 'auto',
  width = 160,
  alt = 'Billda',
  className = '',
}) => {
  const theme = useTheme();

  // Determine which logo to show
  const logoVariant = variant === 'auto'
    ? (theme.palette.mode === 'dark' ? 'secondary' : 'primary')
    : variant;

  // Get the appropriate logo path
  const getLogoSrc = () => {
    // Import paths are resolved at build time
    // For runtime, we'll use the assets from the shared-ui package
    switch (logoVariant) {
      case 'primary':
        return new URL('../assets/logos/primary.svg', import.meta.url).href;
      case 'secondary':
        return new URL('../assets/logos/secondary.svg', import.meta.url).href;
      case 'icon':
        return new URL('../assets/logos/icon-only.svg', import.meta.url).href;
      default:
        return new URL('../assets/logos/primary.svg', import.meta.url).href;
    }
  };

  return (
    <img
      src={getLogoSrc()}
      alt={alt}
      className={className}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: 'auto',
        display: 'block',
        margin: '0 auto',
      }}
    />
  );
};
